import React, { useCallback, useEffect, useMemo } from 'react';

import { OrganizationTypes } from '@/common/constants';
import ROUTES from '@/common/routes';
import { useCurrentOrganization, useHasAccess, useHistory } from '@/modules/core/hooks';
import { useIsApplyUnmatchedPushAvailable } from '@/modules/featureFlags';

import useGetUnappliedPushTotalBalanceQuery from './queries/useGetUnappliedPushTotalBalance.query';
import { UnappliedFundsBanner } from './components';
import { useUnappliedFundsBanner } from './hooks';
import { useUnappliedFundsHomeBannerStore } from './stores';
import { UnappliedFundsBannerVariant } from './UnappliedFundsBanner.types';

interface Props {
  variant: UnappliedFundsBannerVariant;
  permissions: string[];
  organizationId?: string;
  top?: string;
}

const UnappliedFundsBannerContainer: React.FC<Props> = ({ variant, permissions, organizationId, top }) => {
  const history = useHistory();
  const { observingOrganization, observingDefault } = useCurrentOrganization();
  const isFeatureAvailable = useIsApplyUnmatchedPushAvailable();
  const accessRequirements = useMemo(() => permissions.map((permission) => ({ permission })), [permissions]);
  const hasAccess = useHasAccess(accessRequirements);

  const currentOrganizationId = organizationId || observingOrganization?.id;
  const isHomeVariant = variant === UnappliedFundsBannerVariant.HOME;
  const isHiddenOnSystemLevelHome = isHomeVariant && observingDefault?.type === OrganizationTypes.system;
  const isAllowed = useMemo(
    () => isFeatureAvailable && hasAccess && !!currentOrganizationId && !isHiddenOnSystemLevelHome,
    [isFeatureAvailable, hasAccess, currentOrganizationId, isHiddenOnSystemLevelHome]
  );

  const { formattedData } = useGetUnappliedPushTotalBalanceQuery({
    observedOrganizationId: observingOrganization?.id,
    organizationId,
    enabled: isAllowed,
  });

  const { isVisible, isOrganizationsList, organizationNames, organizationsCount } = useUnappliedFundsBanner({
    isAllowed,
    amount: formattedData.amount,
    organizationNames: formattedData.organizationNames,
    organizationsCount: formattedData.organizationsCount,
    currentOrganizationName: observingOrganization?.name,
  });

  const setIsHomeBannerVisible = useUnappliedFundsHomeBannerStore((state) => state.setIsVisible);

  useEffect(() => {
    if (!isHomeVariant) return undefined;
    setIsHomeBannerVisible(isVisible);
    return () => setIsHomeBannerVisible(false);
  }, [isHomeVariant, isVisible, setIsHomeBannerVisible]);

  const handleViewOrganizations = useCallback(() => history.push(ROUTES.UNAPPLIED_FUNDS), [history]);

  const handleViewDetails = useCallback(
    () => history.push(ROUTES.UNAPPLIED_FUNDS_ORG_DETAILS(currentOrganizationId)),
    [history, currentOrganizationId]
  );

  if (!isVisible) {
    return null;
  }

  return (
    <UnappliedFundsBanner
      variant={variant}
      top={top}
      amount={formattedData.amount}
      isOrganizationsList={isOrganizationsList}
      organizationNames={organizationNames}
      organizationsCount={organizationsCount}
      onViewOrganizations={handleViewOrganizations}
      onViewDetails={handleViewDetails}
    />
  );
};

export default UnappliedFundsBannerContainer;
